import "@fontsource/playfair-display/400.css";
import "@fontsource/playfair-display/500.css";
import "@fontsource/playfair-display/600.css";
import "@fontsource/playfair-display/700.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";

import "@/App.css";
import "@/index.css";

import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { LanguageProvider } from "./context/LanguageContext";

// Layout Components
import { Navigation } from "./components/layout/Navigation";
import { Footer } from "./components/layout/Footer";
import { SubsidiaryLayout } from "./components/subsidiary/SubsidiaryLayout";
import { CookieConsent } from "./components/CookieConsent";

// Main Site Pages
import Home from "./pages/Home";
import UeberUns from "./pages/UeberUns";
import Unternehmen from "./pages/Unternehmen";
import Kontakt from "./pages/Kontakt";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import AtoBLogistic from "./pages/AtoBLogistic";
import DSBBusiness from "./pages/DSBBusiness";
import AtoBObjektservice from "./pages/AtoBObjektservice";
import StrategischeDistribution from "./pages/StrategischeDistribution";
import Karriere from "./pages/Karriere";

// AtoB Logistic Subsidiary Pages
import AtoBLogisticHome from "./pages/subsidiary/atob-logistic/Home";
import AtoBLogisticLeistungen from "./pages/subsidiary/atob-logistic/Leistungen";
import AtoBLogisticVermietung from "./pages/subsidiary/atob-logistic/Vermietung";
import AtoBLogisticVermietungBuchen from "./pages/subsidiary/atob-logistic/VermietungBuchen";
import AtoBLogisticBuchungBestaetigung from "./pages/subsidiary/atob-logistic/BuchungBestaetigung";
import AtoBLogisticKontakt from "./pages/subsidiary/atob-logistic/Kontakt";

// DSB Business Subsidiary Pages
import DSBBusinessHome from "./pages/subsidiary/dsb-business/Home";
import DSBBusinessLeistungen from "./pages/subsidiary/dsb-business/Leistungen";
import DSBBusinessKontakt from "./pages/subsidiary/dsb-business/Kontakt";

// AtoB Objektservice Subsidiary Pages
import AtoBObjektserviceHome from "./pages/subsidiary/atob-objektservice/Home";
import AtoBObjektserviceLeistungen from "./pages/subsidiary/atob-objektservice/Leistungen";
import AtoBObjektserviceKontakt from "./pages/subsidiary/atob-objektservice/Kontakt";

// Subsidiary Configs
const ATOB_LOGISTIC_CONFIG = {
  id: 'atob-logistic',
  name: 'AtoB Logistic Solutions GmbH',
  basePath: '/atob-logistic',
  logo: '/images/atob-logistic-logo-transparent.png',
  accentColor: '#F5A623',
  tagline: { de: 'Ihr zuverlässiger Partner für professionelle Logistiklösungen – von A bis B.', en: 'Your reliable partner for professional logistics solutions – from A to B.' },
  email: 'info@atob-logistics.de',
  website: 'https://www.atob-logistics.de',
  address: 'Hanauer Landstraße 291A, 60314 Frankfurt am Main',
  navLinks: [
    { href: '/atob-logistic', label: { de: 'Startseite', en: 'Home' } },
    { href: '/atob-logistic/leistungen', label: { de: 'Leistungen', en: 'Services' } },
    { href: '/atob-logistic/vermietung', label: { de: 'Vermietung', en: 'Rental' } },
    { href: '/atob-logistic/kontakt', label: { de: 'Kontakt', en: 'Contact' } },
  ],
};

const DSB_BUSINESS_CONFIG = {
  id: 'dsb-business',
  name: 'DSB Business Solutions GmbH',
  basePath: '/dsb-business',
  logo: '/images/edited-dsb-logo.png',
  accentColor: '#F5A623',
  tagline: { de: 'Umfassende Beratungsdienstleistungen für den deutschen Mittelstand.', en: 'Comprehensive consulting services for German SMEs.' },
  email: 'info@dsb-deutschland.org',
  website: 'https://www.dsb-deutschland.org',
  address: 'Hanauer Landstraße 291A, 60314 Frankfurt am Main',
  navLinks: [
    { href: '/dsb-business', label: { de: 'Startseite', en: 'Home' } },
    { href: '/dsb-business/leistungen', label: { de: 'Leistungen', en: 'Services' } },
    { href: '/dsb-business/kontakt', label: { de: 'Kontakt', en: 'Contact' } },
  ],
};

const ATOB_OBJEKTSERVICE_CONFIG = {
  id: 'atob-objektservice',
  name: 'AtoB Objektservice GmbH',
  basePath: '/atob-objektservice',
  logo: '/images/atob-objektservice-logo-light.png',
  accentColor: '#F5A623',
  tagline: { de: 'Professionelle Entrümpelung und Entsorgung – schnell, sauber und umweltbewusst.', en: 'Professional clearance and disposal – fast, clean and eco-friendly.' },
  email: 'info@atob-objektservice.de',
  website: 'https://www.atob-objektservice.de',
  address: 'Hanauer Landstraße 291A, 60314 Frankfurt am Main',
  navLinks: [
    { href: '/atob-objektservice', label: { de: 'Startseite', en: 'Home' } },
    { href: '/atob-objektservice/leistungen', label: { de: 'Leistungen', en: 'Services' } },
    { href: '/atob-objektservice/kontakt', label: { de: 'Kontakt', en: 'Contact' } },
  ],
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function MainLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <div className="flex-1">
        <Outlet />
      </div>
      <Footer />
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Main Basic United Holding Site */}
          <Route element={<MainLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/ueber-uns" element={<UeberUns />} />
            <Route path="/unternehmen" element={<Unternehmen />} />
            <Route path="/unternehmen/atob-logistic" element={<AtoBLogistic />} />
            <Route path="/unternehmen/dsb-business" element={<DSBBusiness />} />
            <Route path="/unternehmen/atob-objektservice" element={<AtoBObjektservice />} />
            <Route path="/leistungen/strategische-distribution" element={<StrategischeDistribution />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/karriere" element={<Karriere />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
          </Route>

        {/* AtoB Logistic Solutions Website */}
        <Route element={<SubsidiaryLayout config={ATOB_LOGISTIC_CONFIG} />}>
          <Route path="/atob-logistic" element={<AtoBLogisticHome />} />
          <Route path="/atob-logistic/leistungen" element={<AtoBLogisticLeistungen />} />
          <Route path="/atob-logistic/vermietung" element={<AtoBLogisticVermietung />} />
          <Route path="/atob-logistic/vermietung/buchen/:vehicleId" element={<AtoBLogisticVermietungBuchen />} />
          <Route path="/atob-logistic/vermietung/bestaetigung" element={<AtoBLogisticBuchungBestaetigung />} />
          <Route path="/atob-logistic/kontakt" element={<AtoBLogisticKontakt />} />
        </Route>

        {/* DSB Business Solutions Website */}
        <Route element={<SubsidiaryLayout config={DSB_BUSINESS_CONFIG} />}>
          <Route path="/dsb-business" element={<DSBBusinessHome />} />
          <Route path="/dsb-business/leistungen" element={<DSBBusinessLeistungen />} />
          <Route path="/dsb-business/kontakt" element={<DSBBusinessKontakt />} />
        </Route>

        {/* AtoB Objektservice Website */}
        <Route element={<SubsidiaryLayout config={ATOB_OBJEKTSERVICE_CONFIG} />}>
          <Route path="/atob-objektservice" element={<AtoBObjektserviceHome />} />
          <Route path="/atob-objektservice/leistungen" element={<AtoBObjektserviceLeistungen />} />
          <Route path="/atob-objektservice/kontakt" element={<AtoBObjektserviceKontakt />} />
        </Route>
      </Routes>
    </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
